import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";
import type {
  RelatorioConsumoInsight,
  RelatorioConsumoMediaDiaSemana,
  SituacaoEstoqueInsight,
} from "../../../services/controleProducaoService";
import { groupByUndNome } from "./groupByUnd";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(inicio: string, fim: string): string {
  return `${inicio}_${fim}`;
}

function formatNum(n: number | null | undefined, und?: string): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const txt = Number.isInteger(n)
    ? n.toLocaleString("pt-BR")
    : n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
  return und ? `${txt} ${und}` : txt;
}

function labelSituacaoEstoque(
  situacao: SituacaoEstoqueInsight,
  saldoFinal: number,
  undNome: string
): string {
  if (situacao === "sobra") {
    return `Sobrou ${formatNum(Math.abs(saldoFinal), undNome)}`;
  }
  if (situacao === "faltou") {
    return `Faltou ${formatNum(Math.abs(saldoFinal), undNome)}`;
  }
  return "Zerou no último dia";
}

export interface RelatorioConsumoExportInput {
  lojaNome: string;
  periodoInicioISO: string;
  periodoFimISO: string;
  margemCompraPercent: number;
  diasAbertosNoPeriodo: number;
  insights: RelatorioConsumoInsight[];
  mediasPorDiaSemana: RelatorioConsumoMediaDiaSemana[];
  geradoEmLabel: string;
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

function buildMetaRows(input: RelatorioConsumoExportInput): string[][] {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);
  return [
    ["Período", `${pIni} a ${pFim}`],
    ["Loja", input.lojaNome],
    ["Margem de compra", `+${input.margemCompraPercent}%`],
    ["Dias abertos no período", String(input.diasAbertosNoPeriodo)],
    ["Itens cadastrados", String(input.insights.length)],
    ["Gerado em", input.geradoEmLabel],
  ];
}

function insightRows(insights: RelatorioConsumoInsight[]) {
  return insights.map((ins) => [
    ins.undNome,
    ins.descricao,
    labelSituacaoEstoque(ins.situacaoEstoque, ins.saldoFinal, ins.undNome),
    ins.estoqueInicialPeriodo != null
      ? formatNum(ins.estoqueInicialPeriodo, ins.undNome)
      : "—",
    formatNum(ins.producaoTotal, ins.undNome),
    formatNum(ins.consumoTotal, ins.undNome),
    ins.sobraFinal != null
      ? formatNum(ins.sobraFinal, ins.undNome)
      : "—",
    formatNum(ins.consumoMedioDia, ins.undNome),
    formatNum(ins.sugestaoCompraDia, ins.undNome),
    formatNum(ins.sugestaoCompraPeriodo, ins.undNome),
  ]);
}

function mediaRows(medias: RelatorioConsumoMediaDiaSemana[]) {
  return medias.map((m) => [
    m.undNome,
    m.diaSemanaLabel,
    m.descricao,
    formatNum(m.consumoMedio, m.undNome),
    formatNum(m.consumoTotal, m.undNome),
    formatNum(m.producaoMedia, m.undNome),
    formatNum(m.sobraMedia, m.undNome),
    formatNum(m.sugestaoCompra, m.undNome),
  ]);
}

const INSIGHT_HEAD = [
  "Unidade",
  "Item",
  "Situação",
  "Est. inicial",
  "Produção total",
  "Consumo total",
  "Sobra (último dia)",
  "Média/dia",
  "Sugestão/dia",
  "Sugestão período",
];

const MEDIA_HEAD = [
  "Unidade",
  "Dia",
  "Item",
  "Consumo médio",
  "Consumo total",
  "Produção média",
  "Sobra média",
  "Sugestão",
];

export function exportRelatorioConsumoXlsx(
  input: RelatorioConsumoExportInput
): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const resumoRows: (string | number)[][] = [
    ["Dom Belloni"],
    ["Relatório de Consumo e Produção"],
    [],
    ...buildMetaRows(input),
  ];

  const wsResumo = XLSX.utils.aoa_to_sheet(resumoRows);
  wsResumo["!cols"] = [{ wch: 28 }, { wch: 48 }];

  const wsInsights = XLSX.utils.aoa_to_sheet([
    [`Período: ${pIni} a ${pFim}`],
    ["Quantidades separadas por unidade de medida"],
    [],
    INSIGHT_HEAD,
    ...insightRows(
      groupByUndNome(input.insights, (i) => i.undNome).flatMap((g) => g.items)
    ),
  ]);
  wsInsights["!cols"] = [
    { wch: 12 },
    { wch: 28 },
    { wch: 22 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 16 },
  ];

  const wsMedias = XLSX.utils.aoa_to_sheet([
    [`Período: ${pIni} a ${pFim}`],
    [`Margem: +${input.margemCompraPercent}%`],
    ["Quantidades separadas por unidade de medida"],
    [],
    MEDIA_HEAD,
    ...mediaRows(
      groupByUndNome(input.mediasPorDiaSemana, (m) => m.undNome).flatMap(
        (g) => g.items
      )
    ),
  ]);
  wsMedias["!cols"] = [
    { wch: 12 },
    { wch: 16 },
    { wch: 28 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 12 },
    { wch: 14 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");
  XLSX.utils.book_append_sheet(wb, wsInsights, "Insights");
  XLSX.utils.book_append_sheet(wb, wsMedias, "Médias referência");
  XLSX.writeFile(
    wb,
    `relatorio-consumo-${fileDateSuffix(input.periodoInicioISO, input.periodoFimISO)}.xlsx`
  );
}

function addPdfFooters(doc: jsPDF, geradoEmLabel: string, margin: number): void {
  const pageH = doc.internal.pageSize.getHeight();
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }
}

function drawPdfHeader(
  doc: jsPDF,
  margin: number,
  pageW: number,
  primary: [number, number, number]
): number {
  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Consumo e Produção", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Controle de produção", margin, 19);
  return 32;
}

export function exportRelatorioConsumoPdf(
  input: RelatorioConsumoExportInput
): void {
  const primary = hexToRgb(COLORS.PRIMARY);
  const margin = 14;

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  const docExt = doc as JsPdfWithAutoTable;
  const pageW = doc.internal.pageSize.getWidth();

  let startY = drawPdfHeader(doc, margin, pageW, primary);

  autoTable(doc, {
    startY,
    body: buildMetaRows(input),
    theme: "plain",
    styles: { fontSize: 9, textColor: [42, 42, 42] as [number, number, number], cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 42 },
      1: { cellWidth: pageW - margin * 2 - 42 },
    },
    margin: { left: margin, right: margin },
  });

  let y = (docExt.lastAutoTable?.finalY ?? startY) + 8;

  const tableStyles = {
    theme: "striped" as const,
    headStyles: {
      fillColor: primary,
      textColor: 255 as const,
      fontStyle: "bold" as const,
      halign: "left" as const,
      valign: "middle" as const,
    },
    bodyStyles: {
      textColor: [38, 38, 38] as [number, number, number],
      lineColor: [230, 230, 230] as [number, number, number],
    },
    alternateRowStyles: {
      fillColor: [255, 252, 251] as [number, number, number],
    },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
      lineWidth: 0.08,
    },
  };

  if (input.insights.length > 0) {
    for (const grupo of groupByUndNome(input.insights, (i) => i.undNome)) {
      if (y > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        y = margin;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(42, 42, 42);
      doc.text(`Insights para compra · ${grupo.undNome}`, margin, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        head: [INSIGHT_HEAD],
        body: insightRows(grupo.items),
        ...tableStyles,
      });
      y = (docExt.lastAutoTable?.finalY ?? y) + 8;
    }
  }

  if (input.mediasPorDiaSemana.length > 0) {
    for (const grupo of groupByUndNome(
      input.mediasPorDiaSemana,
      (m) => m.undNome
    )) {
      if (y > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        y = margin;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(42, 42, 42);
      doc.text(
        `Médias de referência · ${grupo.undNome} (+${input.margemCompraPercent}%)`,
        margin,
        y
      );
      y += 4;

      autoTable(doc, {
        startY: y,
        head: [MEDIA_HEAD],
        body: mediaRows(grupo.items),
        ...tableStyles,
      });
      y = (docExt.lastAutoTable?.finalY ?? y) + 8;
    }
  }

  addPdfFooters(doc, input.geradoEmLabel, margin);
  doc.save(
    `relatorio-consumo-${fileDateSuffix(input.periodoInicioISO, input.periodoFimISO)}.pdf`
  );
}
